import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Ban, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { VoucherResponse } from '@/app/types/voucher';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';

// Helper function to format currency
const formatCurrency = (amount: string | number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number(amount));
};

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch (error) {
    return String(dateString); // Fallback
  }
};

// Type for action handlers passed to columns
interface ExpenseActionHandlers {
    onEdit: (id: number) => void;
    onCancel: (id: number) => void;
    onView?: (id: number) => void;
}

export const expenseColumns = ({ onEdit, onCancel, onView }: ExpenseActionHandlers): ColumnDef<VoucherResponse>[] => [
  {
    accessorKey: 'voucher_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Number" />,
    cell: ({ row }) => {
      const navigate = useNavigate();
      const voucherId = row.original.id;
      return (
        <Button
          variant="link"
          className="p-0 h-auto font-medium text-primary hover:underline"
          onClick={() => navigate(`/expenses/detail/${voucherId}`)}
        >
          {row.getValue('voucher_number')}
        </Button>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: 'Voucher #',
      variant: 'text',
      placeholder: 'Search voucher number...',
    },
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => <span>{formatDate(row.getValue('transaction_date'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Date',
      variant: 'dateRange',
      placeholder: 'Filter by date...',
    },
  },
  {
    accessorKey: 'reference_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Reference" />,
    cell: ({ row }) => <span>{row.getValue('reference_number') || '-'}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Reference #',
      variant: 'text',
      placeholder: 'Search reference...',
    },
  },
  {
    id: 'site.name',
    accessorFn: (row) => row.site?.name || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    cell: ({ row }) => <span>{row.original.site?.name || '-'}</span>,
    enableSorting: false,
    enableColumnFilter: true,
    meta: {
      label: 'Site',
      variant: 'text',
      placeholder: 'Search site name...',
    },
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => <span className="font-medium text-right block">{formatCurrency(row.getValue('total_amount'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Amount',
      variant: 'number',
      placeholder: 'Filter amount...',
    },
  },
  {
    accessorKey: 'is_cancelled',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Badge variant={row.getValue('is_cancelled') ? 'destructive' : 'secondary'}>
        {row.getValue('is_cancelled') ? 'Cancelled' : 'Active'}
      </Badge>
    ),
    filterFn: (row, id, value) => {
        return value === 'cancelled' ? row.getValue(id) === true : row.getValue(id) === false;
    },
    enableColumnFilter: true,
    meta: {
      label: 'Status',
      variant: 'select',
      placeholder: 'All statuses',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const expense = row.original;
      const isCancelled = expense.is_cancelled;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView?.(expense.id)}>
              <Eye className="mr-2 h-4 w-4" /> View Details
            </DropdownMenuItem>
            {!isCancelled && ( // Only show Edit/Cancel if not already cancelled
                 <>
                    <DropdownMenuItem onClick={() => onEdit(expense.id)}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onCancel(expense.id)} className="text-destructive focus:text-destructive focus:bg-destructive/10">
                        <Ban className="mr-2 h-4 w-4" /> Cancel
                    </DropdownMenuItem>
                 </>
            )}
             {isCancelled && (
                 <DropdownMenuItem disabled>
                     <span className='opacity-50'>Cancelled</span>
                 </DropdownMenuItem>
             )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 